/**
 * Vizitka — uložení kontaktu jako vCard ze skutečného textu na kartě (schema.org / DOM).
 */

const SAVE_BTN_CLASS = 'business-card__save-contact';
const SAVE_BTN_ICO_CLASS = 'business-card__save-contact-ico';
const SAVE_BTN_LABEL_CLASS = 'business-card__save-contact-label';
const ACTIONS_CLASS = 'business-card__actions';
const STAGE_CLASS = 'business-card__stage';
const FACE_CLASS = 'business-card__face';
const SAVE_BTN_LABEL = 'Uložit kontakt';

/** Material Icons „download“ — stejná cesta jako svgIcoDownload v www_svg-icons. */
const SAVE_BTN_ICO_HTML =
    `<svg class="ico ico--download ${SAVE_BTN_ICO_CLASS}" xmlns="http://www.w3.org/2000/svg" height="24" viewBox="0 0 24 24" width="24" fill="currentColor" aria-hidden="true" focusable="false"><path d="M0 0h24v24H0V0z" fill="none"/><path d="M16.59 9H15V4c0-.55-.45-1-1-1h-4c-.55 0-1 .45-1 1v5H7.41c-.89 0-1.34 1.08-.71 1.71l4.59 4.59c.39.39 1.02.39 1.41 0l4.59-4.59c.63-.63.19-1.71-.7-1.71zM5 19c0 .55.45 1 1 1h12c.55 0 1-.45 1-1s-.45-1-1-1H6c-.55 0-1 .45-1 1z"/></svg>`;

interface IBusinessCardContact {
    name: string;
    telephone: string;
    email: string;
    url: string;
    org: string;
    jobTitle: string;
}

const escapeVCardValue = (value: string): string => {
    return value.replace(/\\/g, '\\\\').replace(/;/g, '\\;').replace(/,/g, '\\,').replace(/\r?\n/g, '\\n');
};

const textOrContent = (el: Element | null): string => {
    if (!el) {
        return '';
    }
    const content = el.getAttribute('content');
    if (content) {
        return content.trim();
    }
    return (el.textContent ?? '').trim();
};

const firstMatch = (card: HTMLElement, selectors: string[]): Element | null => {
    for (const selector of selectors) {
        const el = card.querySelector(selector);
        if (el) {
            return el;
        }
    }
    return null;
};

const readTelephone = (el: Element | null): string => {
    if (el instanceof HTMLAnchorElement) {
        const href = el.getAttribute('href') ?? '';
        if (href.toLowerCase().startsWith('tel:')) {
            return decodeURIComponent(href.replace(/^tel:/i, ''));
        }
    }
    return textOrContent(el).replace(/\s+/g, '');
};

const readEmail = (el: Element | null): string => {
    if (el instanceof HTMLAnchorElement) {
        const href = el.getAttribute('href') ?? '';
        if (href.toLowerCase().startsWith('mailto:')) {
            return decodeURIComponent(href.replace(/^mailto:/i, '').split('?')[0] ?? '');
        }
    }
    return textOrContent(el);
};

const readUrl = (el: Element | null): string => {
    if (el instanceof HTMLAnchorElement && el.href) {
        return el.href;
    }
    const raw = textOrContent(el);
    if (!raw) {
        return '';
    }
    return /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
};

const readContactFromCard = (card: HTMLElement): IBusinessCardContact => {
    // itemprop často TinyMCE vyhodí — fallback na BEM třídy (ostrý obsah v CMS)
    const personName = firstMatch(card, [
        '.business-card__name[itemprop="name"]',
        '.business-card__name',
        '.business-card__main [itemprop="name"]',
    ]);

    const telEl = firstMatch(card, [
        '[itemprop="telephone"]',
        '.business-card__tel a',
        '.business-card__tel',
    ]);
    const emailEl = firstMatch(card, ['[itemprop="email"]', '.business-card__email a', '.business-card__email']);
    const urlEl = firstMatch(card, ['[itemprop="url"]', '.business-card__web a', '.business-card__web']);
    const orgEl = firstMatch(card, ['[itemprop="worksFor"] [itemprop="name"]']);
    const jobEl = firstMatch(card, ['[itemprop="jobTitle"]']);

    return {
        name: textOrContent(personName),
        telephone: readTelephone(telEl),
        email: readEmail(emailEl),
        url: readUrl(urlEl),
        org: textOrContent(orgEl),
        jobTitle: textOrContent(jobEl),
    };
};

const buildVCard = (contact: IBusinessCardContact): string => {
    const lines: string[] = ['BEGIN:VCARD', 'VERSION:3.0'];

    if (contact.name) {
        lines.push(`FN:${escapeVCardValue(contact.name)}`);
        const parts = contact.name.split(/\s+/);
        const last = parts.length > 1 ? (parts[parts.length - 1] ?? '') : '';
        const first = parts.length > 1 ? parts.slice(0, -1).join(' ') : contact.name;
        lines.push(`N:${escapeVCardValue(last)};${escapeVCardValue(first)};;;`);
    }
    if (contact.org) {
        lines.push(`ORG:${escapeVCardValue(contact.org)}`);
    }
    if (contact.jobTitle) {
        lines.push(`TITLE:${escapeVCardValue(contact.jobTitle)}`);
    }
    if (contact.telephone) {
        lines.push(`TEL;TYPE=CELL,VOICE:${escapeVCardValue(contact.telephone)}`);
    }
    if (contact.email) {
        lines.push(`EMAIL;TYPE=INTERNET:${escapeVCardValue(contact.email)}`);
    }
    if (contact.url) {
        lines.push(`URL:${escapeVCardValue(contact.url)}`);
    }

    lines.push('END:VCARD');
    return `${lines.join('\r\n')}\r\n`;
};

const slugifyFilename = (name: string): string => {
    const slug = name
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
    return slug || 'kontakt';
};

const isMobileSharePreferred = (): boolean => {
    if (typeof window.matchMedia === 'function' && window.matchMedia('(pointer: coarse)').matches) {
        return true;
    }
    return /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
};

/** Synchrónní download — musí běžet přímo v user-gesture (click), ne po await. */
const downloadVCard = (vcf: string, filename: string): void => {
    const blob = new Blob([vcf], { type: 'text/vcard;charset=utf-8' });
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = filename;
    // display:none v Chrome často zruší download — odsunout mimo viewport
    link.style.position = 'fixed';
    link.style.left = '-9999px';
    link.style.top = '0';
    document.body.appendChild(link);
    link.click();
    window.setTimeout(() => {
        link.remove();
        URL.revokeObjectURL(objectUrl);
    }, 2000);
};

const tryShareVCard = async (file: File, title: string): Promise<boolean> => {
    const nav = navigator as Navigator & {
        canShare?: (data?: ShareData) => boolean;
    };

    if (typeof nav.share !== 'function' || typeof nav.canShare !== 'function') {
        return false;
    }

    try {
        if (!nav.canShare({ files: [file] })) {
            return false;
        }
        await nav.share({
            files: [file],
            title,
            text: title,
        });
        return true;
    } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') {
            return true;
        }
        return false;
    }
};

const saveContactFromCard = (card: HTMLElement): void => {
    const contact = readContactFromCard(card);
    if (!contact.name && !contact.telephone && !contact.email) {
        return;
    }

    const vcf = buildVCard(contact);
    const filename = `${slugifyFilename(contact.name)}.vcf`;

    // Desktop Chrome: hned stahovat (share s files je nespolehlivý / „nic nedělá“)
    if (!isMobileSharePreferred()) {
        downloadVCard(vcf, filename);
        return;
    }

    const file = new File([vcf], filename, { type: 'text/vcard' });
    const title = contact.name || 'Kontakt';
    void tryShareVCard(file, title).then((shared) => {
        if (!shared) {
            downloadVCard(vcf, filename);
        }
    });
};

/** Obal pro mobilní scale — doplní se i u staršího HTML z CMS. */
const ensureStage = (card: HTMLElement): void => {
    const face = card.querySelector(`.${FACE_CLASS}`);
    if (!(face instanceof HTMLElement)) {
        return;
    }
    if (face.parentElement?.classList.contains(STAGE_CLASS)) {
        return;
    }
    const stage = document.createElement('div');
    stage.className = STAGE_CLASS;
    face.replaceWith(stage);
    stage.appendChild(face);
};

const fillSaveButtonContent = (button: HTMLButtonElement): void => {
    if (button.querySelector(`.${SAVE_BTN_ICO_CLASS}`)) {
        if (!button.querySelector(`.${SAVE_BTN_LABEL_CLASS}`)) {
            const label = document.createElement('span');
            label.className = SAVE_BTN_LABEL_CLASS;
            label.textContent = button.textContent?.trim() || SAVE_BTN_LABEL;
            // textové uzly mimo label smažeme — zůstane ikona + label
            for (const node of Array.from(button.childNodes)) {
                if (node.nodeType === Node.TEXT_NODE) {
                    node.remove();
                }
            }
            button.appendChild(label);
        }
        return;
    }

    const existingLabel = button.querySelector(`.${SAVE_BTN_LABEL_CLASS}`);
    const labelText = (existingLabel?.textContent ?? button.textContent)?.trim() || SAVE_BTN_LABEL;
    button.replaceChildren();
    button.insertAdjacentHTML('afterbegin', SAVE_BTN_ICO_HTML);
    const label = document.createElement('span');
    label.className = SAVE_BTN_LABEL_CLASS;
    label.textContent = labelText;
    button.appendChild(label);
};

const ensureSaveButton = (card: HTMLElement): HTMLButtonElement => {
    let button = card.querySelector(`.${SAVE_BTN_CLASS}`) as HTMLButtonElement | null;
    if (button) {
        fillSaveButtonContent(button);
        return button;
    }

    let actions = card.querySelector(`.${ACTIONS_CLASS}`) as HTMLElement | null;
    if (!actions) {
        actions = document.createElement('div');
        actions.className = ACTIONS_CLASS;
        card.appendChild(actions);
    }

    button = document.createElement('button');
    button.type = 'button';
    button.className = `btn btn-primary ${SAVE_BTN_CLASS}`;
    fillSaveButtonContent(button);
    actions.appendChild(button);
    return button;
};

export const initBusinessCards = (root: ParentNode = document): void => {
    const cards = root.querySelectorAll('.business-card');
    for (const cardEl of Array.from(cards)) {
        const card = cardEl as HTMLElement;
        if (card.dataset['vcardBound'] === '1') {
            continue;
        }
        card.dataset['vcardBound'] = '1';

        ensureStage(card);

        const button = ensureSaveButton(card);
        button.addEventListener('click', (e: Event): void => {
            e.preventDefault();
            e.stopPropagation();
            saveContactFromCard(card);
        });
    }
};
